import { UNASSIGNED, squadChain, type GroupBy, type GroupNode, type Squad, type SquadRole } from '@tive/core';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Check, ChevronDown, ChevronUp, FolderTree, Palette, Pencil, Trash2, X } from 'lucide-react';
import { memo, useState, type DragEvent } from 'react';
import { SERIES_COLORS, colorKeyFor, swatchFor } from '../lib/colors';
import { matchesFilters } from '../lib/filters';
import { GROUP_CARD_WIDTH, groupCardHeight, memberListHeight } from '../lib/group-layout';
import { canDropOnSquad, squadRoleBadge } from '../lib/squads';
import { resolveTheme } from '../lib/theme';
import { cn, departmentColor } from '../lib/utils';
import { useAppStore } from '../store/useAppStore';
import { Avatar } from './Avatar';
import { Badge, IconButton, Input } from './ui';

/** How far the fold pill hangs below the card, and so where a line may start. */
const PILL_CLEARANCE = 15;

/*
 * Two different things are called a team, so the labels have to be precise:
 * `squad` is the one you build here (Artemis), `team` is whatever the HRIS
 * happens to carry in its own team column.
 */
export const LEVEL_LABELS: Record<GroupBy, string> = {
  org: 'Org',
  department: 'Department',
  squad: 'Team',
  team: 'HRIS team',
  location: 'Location',
  country: 'Country',
};

export interface GroupCardData extends Record<string, unknown> {
  node: GroupNode;
  /** Children in the tree, folded or not — what the pill counts. */
  childCount: number;
  folded: boolean;
}

/**
 * One box on the team board, at every level of the grouping.
 *
 * Org, department and team are all cards now, and the difference between them
 * is only what they carry: the levels above the deepest have no members, so
 * they are short cards whose job is to be the thing their row hangs off. Before
 * this they were section headings, which made the board a tree drawn as an
 * outline — and an outline cannot say which department a team belongs to once
 * the two are a screen apart.
 *
 * The level's own name rides above the value for the same reason. "Dave Perri"
 * alone is a person, a location or an org depending on what the toolbar is set
 * to, and the toolbar is off the top of the canvas as soon as you pan.
 */
export const GroupCard = memo(function GroupCard({ data }: NodeProps & { data: GroupCardData }) {
  const { node, childCount, folded } = data;
  const squad = useSquadBehind(node);
  const toggleGroupFold = useAppStore((s) => s.toggleGroupFold);
  const setBoardDrag = useAppStore((s) => s.setBoardDrag);

  const proposed = useAppStore((s) =>
    squad ? s.changes.some((change) => change.id === `squad-add:${squad.id}`) : false,
  );
  // The team's own colour, whether pinned or hashed, so the board reads the
  // same way the chart does when it is coloured by team.
  const accent = useAppStore((s) => (node.key === 'squad' ? swatchFor(node.label, s.colorOverrides) : null));
  const planning = useAppStore((s) => s.teamEditTarget === 'scenario');
  const renameSquad = useAppStore((s) => s.renameSquad);
  const deleteSquad = useAppStore((s) => s.deleteSquad);

  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(node.label);

  const drop = useDropTarget(squad);

  const commitRename = () => {
    const name = draft.trim();
    if (squad && name && name !== squad.name) void renameSquad(squad.id, name);
    setRenaming(false);
  };

  return (
    <article
      /*
       * A team card can be picked up and dropped on another, the way a card on
       * the chart can be dropped on a manager. Only a real team: the levels
       * above one are values of an HRIS column, and there is nothing to move.
       *
       * Native drag rather than React Flow's, because the same gesture has to
       * work for a member row inside the card, and React Flow only knows about
       * whole nodes. `nopan` already stops the canvas panning underneath.
       */
      draggable={Boolean(squad) && !renaming}
      onDragStart={(event) => {
        if (!squad) return;
        event.dataTransfer.effectAllowed = 'move';
        // Firefox starts no drag at all without something on the transfer.
        event.dataTransfer.setData('text/plain', squad.name);
        setBoardDrag({ kind: 'squad', squadId: squad.id, name: squad.name });
      }}
      onDragEnd={() => setBoardDrag(null)}
      {...drop.handlers}
      style={{
        width: GROUP_CARD_WIDTH,
        height: groupCardHeight({ memberCount: node.members.length, childIds: node.childIds }),
        /*
         * React Flow turns pointer events off on the wrapper of any node that
         * is not draggable or selectable, and this card is neither — it is a
         * box of buttons, a scrolling member list and a hover state, all of
         * which were dead without this. Panning is unaffected: a wheel event
         * bubbles out to the canvas whatever is under the pointer.
         *
         * Inline rather than a Tailwind class, because jsdom loads no
         * stylesheet: a class here is invisible to the component tests, which
         * would then see the wrapper's `none` and refuse every click.
         */
        pointerEvents: 'auto',
        ...(accent ? { borderLeftColor: accent, borderLeftWidth: 6 } : {}),
      }}
      className={cn(
        /*
         * `nopan`: a drag that starts on a card does not pan the canvas. The
         * card is a box of buttons and a scrolling list, so a drag on it is far
         * more likely to be someone selecting a name than someone moving the
         * board — and panning is still a scroll away, which works over a card
         * and everywhere else. It also keeps d3-zoom's mousedown handler out of
         * every component test, where jsdom hands it an event with no view and
         * it throws.
         */
        'group/box nopan relative rounded-xl border bg-[var(--color-surface)] px-2.5 pt-2.5',
        'shadow-[var(--shadow-card)]',
        proposed
          ? 'border-dashed border-[var(--color-fresh)] ring-2 ring-[var(--color-fresh)]/30'
          : 'border-[var(--color-line)]',
        drop.over && 'ring-2 ring-[var(--color-brand)]',
      )}
    >
      {/*
        Three targets, because a card is joined from above when it sits under
        its parent and from the side when it hangs off a stack's trunk. Named
        from both ends, because React Flow silently falls back to the first
        handle on the card when an edge does not say which one it wants.
      */}
      <Handle id="above" type="target" position={Position.Top} isConnectable={false} />
      <Handle id="left" type="target" position={Position.Left} isConnectable={false} />
      <Handle id="right" type="target" position={Position.Right} isConnectable={false} />
      <Handle
        id="below"
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        style={{ bottom: -PILL_CLEARANCE }}
      />

      <p className="truncate text-[9.5px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
        {LEVEL_LABELS[node.key]}
      </p>

      <header className="flex items-center gap-1.5 pb-2">
        {renaming ? (
          <>
            <Input
              autoFocus
              aria-label={`Rename ${node.label}`}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commitRename();
                if (event.key === 'Escape') setRenaming(false);
              }}
              className="h-7 text-[12px]"
            />
            <IconButton label="Save name" className="size-7" onClick={commitRename}>
              <Check size={13} />
            </IconButton>
          </>
        ) : (
          <>
            <h4
              className={cn(
                'min-w-0 flex-1 truncate text-[12.5px] font-semibold text-[var(--color-ink)]',
                node.label === UNASSIGNED && 'font-normal text-[var(--color-ink-3)] italic',
              )}
              title={node.label}
            >
              {node.label}
            </h4>
            {proposed ? <Badge tone="fresh">New</Badge> : null}
            <Badge>{node.headcount}</Badge>
            {squad ? (
              <div className="flex opacity-0 transition-opacity group-hover/box:opacity-100 focus-within:opacity-100">
                <NestButton squad={squad} />
                {/* Only against the base: a colour is a property of the team,
                    not something a plan proposes. */}
                {planning ? null : <ColorButton squad={squad} />}
                <IconButton
                  label={`Rename ${squad.name}`}
                  className="size-7"
                  onClick={() => {
                    setDraft(squad.name);
                    setRenaming(true);
                  }}
                >
                  <Pencil size={12} />
                </IconButton>
                <IconButton
                  /*
                   * Three different things wear the same bin icon, and only one
                   * of them is irreversible:
                   *   in a plan      — stood down, revertable from the change list
                   *   a team we made — actually deleted, so it asks first
                   *   an HRIS team   — only hidden; the next refresh reports it
                   *                    again, so offering to "delete" it would
                   *                    be a promise this app cannot keep
                   */
                  label={
                    planning
                      ? `Stand down ${squad.name}`
                      : squad.fromHris
                        ? `Hide ${squad.name}`
                        : `Delete ${squad.name}`
                  }
                  className="size-7 hover:text-[var(--color-moved)]"
                  onClick={() => {
                    if (
                      planning ||
                      squad.fromHris ||
                      window.confirm(`Delete the team “${squad.name}”? This cannot be undone.`)
                    ) {
                      void deleteSquad(squad.id);
                    }
                  }}
                >
                  <Trash2 size={12} />
                </IconButton>
              </div>
            ) : null}
          </>
        )}
      </header>

      {node.members.length === 0 && childCount === 0 ? (
        <p className="px-1 text-[11px] text-[var(--color-ink-3)]">Nobody on this team yet.</p>
      ) : node.members.length === 0 ? null : (
        /*
         * Scrolled rather than truncated, at exactly the height the layout
         * allowed for it: "Unassigned" can hold three hundred people in a
         * 330-person org, and a box a mile long is no use — but neither is one
         * that hides who is in it.
         */
        <ul
          style={{ maxHeight: memberListHeight(node.members.length) }}
          className="space-y-0.5 overflow-y-auto"
        >
          {node.members.map((member) => (
            <MemberRow
              key={member.personId}
              personId={member.personId}
              role={member.role}
              squadId={squad?.id}
              squadName={squad?.name}
            />
          ))}
        </ul>
      )}

      {/*
        Straddling the bottom edge, the same pill the chart puts on a card, and
        it reads the same way: a number is what is hidden, a chevron up is what
        is showing. Counted in cards rather than people — the headcount badge in
        the header already covers everyone below.
      */}
      {childCount > 0 ? (
        <button
          type="button"
          onClick={() => toggleGroupFold(node.id)}
          /* Labelled as well as titled: the pill's content is a bare number, and
             that is what a screen reader — and a test — would otherwise read. */
          aria-label={foldLabel(node.label, childCount, !folded)}
          title={foldLabel(node.label, childCount, !folded)}
          className={cn(
            'absolute -bottom-3 left-1/2 z-10 flex h-6 -translate-x-1/2 items-center gap-1',
            'rounded-full border border-[var(--color-line)] bg-[var(--color-surface)] px-2',
            'text-[10.5px] font-semibold text-[var(--color-ink-2)] shadow-[var(--shadow-card)]',
            'transition-colors hover:border-[var(--color-brand)] hover:text-[var(--color-brand)]',
            folded && 'border-[var(--color-brand)]/40 text-[var(--color-brand)]',
          )}
        >
          {folded ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
          {folded ? childCount : null}
        </button>
      ) : null}
    </article>
  );
});

/**
 * The drop half of the board's dragging, for one team card.
 *
 * Whether a drop is allowed is decided from the drag in the store rather than
 * from the transfer, because a browser will not let anything read the transfer
 * during `dragover` — which is the only moment a card can say whether it will
 * accept what is over it.
 */
function useDropTarget(squad: Squad | undefined) {
  const drag = useAppStore((s) => s.boardDrag);
  const directory = useAppStore((s) => (s.teamEditTarget === 'base' ? s.squads : s.planSquads));
  const setBoardDrag = useAppStore((s) => s.setBoardDrag);
  const setSquadParent = useAppStore((s) => s.setSquadParent);
  const moveSquadMember = useAppStore((s) => s.moveSquadMember);
  const toast = useAppStore((s) => s.toast);
  const [over, setOver] = useState(false);

  const verdict = squad && drag ? canDropOnSquad(directory, drag, squad.id) : null;

  return {
    over: over && Boolean(verdict?.ok),
    handlers: {
      onDragOver: (event: DragEvent) => {
        if (!verdict?.ok) return;
        // Without this the browser treats the card as somewhere a drop cannot
        // land, and `onDrop` is never called.
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setOver(true);
      },
      onDragLeave: () => setOver(false),
      onDrop: (event: DragEvent) => {
        event.preventDefault();
        setOver(false);
        if (!squad || !drag) return;
        setBoardDrag(null);

        const check = canDropOnSquad(directory, drag, squad.id);
        if (!check.ok) {
          // A refusal with nothing to say is a drop that would change nothing.
          if (check.reason) toast('error', check.reason);
          return;
        }

        if (drag.kind === 'squad') void setSquadParent(drag.squadId, squad.id);
        else void moveSquadMember(drag.fromSquadId, squad.id, drag.personId, drag.role);
      },
    },
  };
}

/** What the fold pill says, in both directions. */
export function foldLabel(name: string, childCount: number, open: boolean): string {
  if (open) return `Hide the teams in ${name}`;
  return `Show ${childCount} ${childCount === 1 ? 'team' : 'teams'} in ${name}`;
}

/**
 * The squad a box stands for.
 *
 * The group tree carries labels, not ids, so it is the one with that name in
 * whichever directory is on screen.
 */
function useSquadBehind(node: GroupNode): Squad | undefined {
  return useAppStore((s) => {
    if (node.key !== 'squad') return undefined;
    const directory = s.teamEditTarget === 'base' ? s.squads : s.planSquads;
    return directory.squads.find((candidate) => candidate.name === node.label);
  });
}

// ---------------------------------------------------------------------------

/**
 * Put a team inside another, or pull it back out to the top level.
 *
 * The list leaves out the team itself and everything already inside it: a team
 * inside its own sub-team has no meaning, and offering the move only to refuse
 * it is worse than not offering it.
 */
function NestButton({ squad }: { squad: Squad }) {
  const squads = useAppStore((s) => (s.teamEditTarget === 'base' ? s.squads : s.planSquads));
  const setSquadParent = useAppStore((s) => s.setSquadParent);
  const [open, setOpen] = useState(false);

  const inside = new Set<string>([squad.id]);
  for (const candidate of squads.squads) {
    if (squadChain(squads, candidate.id).some((step) => step.id === squad.id)) inside.add(candidate.id);
  }
  const options = squads.squads.filter((candidate) => !inside.has(candidate.id));

  const move = (parentId: string | null) => {
    void setSquadParent(squad.id, parentId);
    setOpen(false);
  };

  return (
    <div className="relative">
      <IconButton
        label={`Move ${squad.name} into another team`}
        className="size-7"
        onClick={() => setOpen((v) => !v)}
      >
        <FolderTree size={12} />
      </IconButton>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close move menu"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-8 right-0 z-20 w-52 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] p-1 shadow-[var(--shadow-panel)]">
            <p className="px-2 pt-1 pb-1.5 text-[10px] font-semibold tracking-wide text-[var(--color-ink-3)] uppercase">
              Put {squad.name} inside
            </p>
            <div className="max-h-56 space-y-0.5 overflow-y-auto">
              <button
                type="button"
                onClick={() => move(null)}
                className={cn(
                  'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-[var(--color-surface-2)]',
                  squad.parentId === null ? 'text-[var(--color-ink-3)]' : 'text-[var(--color-ink)]',
                )}
              >
                {squad.parentId === null ? <Check size={12} /> : <span className="size-3" />}
                Nothing — top level
              </button>
              {options.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => move(candidate.id)}
                  className={cn(
                    'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-[var(--color-surface-2)]',
                    squad.parentId === candidate.id ? 'text-[var(--color-ink-3)]' : 'text-[var(--color-ink)]',
                  )}
                >
                  {squad.parentId === candidate.id ? <Check size={12} /> : <span className="size-3" />}
                  <span className="truncate">{candidate.name}</span>
                </button>
              ))}
              {options.length === 0 ? (
                <p className="px-2 py-1.5 text-[11px] text-[var(--color-ink-3)]">
                  No other team to put it inside yet.
                </p>
              ) : null}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** Pin a team's colour, usually to match the one it already has elsewhere. */
function ColorButton({ squad }: { squad: Squad }) {
  const setSquadColor = useAppStore((s) => s.setSquadColor);
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <IconButton label={`Colour ${squad.name}`} className="size-7" onClick={() => setOpen((v) => !v)}>
        <Palette size={12} />
      </IconButton>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close colours"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute top-8 right-0 z-20 w-36 rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] p-1.5 shadow-[var(--shadow-panel)]">
            <div className="flex flex-wrap gap-1">
              {SERIES_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={color}
                  title={color}
                  style={{ background: color }}
                  onClick={() => {
                    void setSquadColor(squad.id, color);
                    setOpen(false);
                  }}
                  className={cn(
                    'size-6 rounded-md border-2',
                    squad.color === color ? 'border-[var(--color-ink)]' : 'border-transparent',
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                void setSquadColor(squad.id, null);
                setOpen(false);
              }}
              className="mt-1 w-full rounded-md px-1.5 py-1 text-left text-[11px] text-[var(--color-brand)] hover:bg-[var(--color-surface-2)]"
            >
              Automatic
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

function MemberRow({
  personId,
  role,
  squadId,
  squadName,
}: {
  personId: string;
  role: SquadRole;
  squadId?: string;
  squadName?: string;
}) {
  const person = useAppStore((s) => s.model?.byId.get(personId));
  const dark = useAppStore((s) => resolveTheme(s.theme) === 'dark');
  // The avatar follows whatever the chart is coloured by, so switching views
  // does not switch what a colour means.
  const colorKey = useAppStore((s) => {
    const target = s.model?.byId.get(personId);
    return target ? colorKeyFor(target, s.colorBy, s.planSquads) : null;
  });
  const overrides = useAppStore((s) => s.colorOverrides);
  const colorBy = useAppStore((s) => s.colorBy);
  const isMoved = useAppStore((s) => s.movedIds.has(personId));
  // Joining or changing hats in the plan, rather than in the base directory.
  const membershipChange = useAppStore((s) => {
    if (!squadId) return undefined;
    const change = s.changes.find((candidate) => candidate.id === `squad-member:${squadId}:${personId}`);
    return change?.kind === 'squad-member' ? change : undefined;
  });
  const isDeparting = useAppStore((s) => s.model?.departures.has(personId) ?? false);
  const isSelected = useAppStore((s) => s.selectedId === personId);
  // Filtered out entirely rather than faded: the board has the same problem
  // the chart had, and the same answer.
  const hidden = useAppStore((s) => s.filtered.keep !== null && !matchesFilters(s, personId));
  const select = useAppStore((s) => s.select);
  const openPanel = useAppStore((s) => s.openPanel);
  const removeSquadMember = useAppStore((s) => s.removeSquadMember);
  const setBoardDrag = useAppStore((s) => s.setBoardDrag);

  if (!person || hidden) return null;
  const dept = departmentColor(person.department, dark);
  const accent = colorKey ? swatchFor(colorKey, overrides, colorBy) : null;
  const roleLabel = squadRoleBadge(role);

  return (
    <li
      /*
       * Dragged onto another team, which *moves* them: off this one and onto
       * that one. Somebody who should be on two teams is added to the second
       * from the details pane instead — dragging says the opposite, and a
       * gesture that meant "copy" here and "move" everywhere else would be the
       * kind of thing people find out by losing a membership.
       *
       * Only from a real team. A member listed on a department card is there
       * because of an HRIS column, and there is nothing to take them off.
       */
      draggable={Boolean(squadId)}
      onDragStart={(event) => {
        if (!squadId) return;
        event.stopPropagation();
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', person.name);
        setBoardDrag({ kind: 'member', personId, name: person.name, fromSquadId: squadId, role });
      }}
      onDragEnd={() => setBoardDrag(null)}
      // The same bar as the chart card, so a colour means one thing in both views.
      style={accent ? { borderLeftColor: accent } : undefined}
      className={cn('group/member flex items-center gap-1', accent && 'border-l-4 pl-1')}
    >
      <button
        type="button"
        onClick={() => {
          select(personId);
          openPanel('details');
        }}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-1.5 py-1 text-left transition-colors',
          'hover:bg-[var(--color-surface-2)]',
          isSelected && 'bg-[var(--color-brand-soft)]',
        )}
      >
        <Avatar person={person} size={24} colors={dept} />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block truncate text-[12px] leading-tight text-[var(--color-ink)]',
              isDeparting && 'line-through decoration-[var(--color-leaving)]',
            )}
          >
            {person.name}
          </span>
          <span className="block truncate text-[10.5px] text-[var(--color-ink-3)]">{person.title}</span>
        </span>
        {isMoved ? <Badge tone="moved">Moved</Badge> : null}
        {person.isNew ? <Badge tone="fresh">New</Badge> : null}
        {membershipChange ? (
          <Badge tone="fresh">{membershipChange.fromRole === null ? 'Joining' : 'Changed'}</Badge>
        ) : null}
        {roleLabel ? <Badge tone={role === 'lead' ? 'brand' : 'neutral'}>{roleLabel}</Badge> : null}
      </button>

      {squadId ? (
        <IconButton
          label={`Remove ${person.name} from ${squadName ?? 'this team'}`}
          className="size-6 opacity-0 group-hover/member:opacity-100 hover:text-[var(--color-moved)] focus-visible:opacity-100"
          onClick={() => void removeSquadMember(squadId, personId)}
        >
          <X size={12} />
        </IconButton>
      ) : null}
    </li>
  );
}
